import React from "react";
import tinycolor from "tinycolor2";
import ActivityBlock from "./ActivityBlock";
import { getContrastTextColor } from "../utils/colorUtils";

/**
 * LoopSegmentRow — one row of loop-compressed activity segments from the backend.
 *
 * Singletons render as plain chips; a loop renders as a connected chip group with
 * an outline coloured from the chips themselves and a small count badge on the
 * corner. The badge number is the median repetition count across the cases in
 * the variant, or "N" in abstract mode — the backend decides which.
 *
 * @param {Object} props
 * @param {Array}  props.loopSegments    - Backend loop_segments array.
 * @param {Object} props.colorPalette    - Map of activity_code to color.
 * @param {Object} props.activityCatalog - Map of activity_code to { code, name }.
 * @param {boolean} props.wrap - Wrap across lines (expanded rows) vs. a single scrolling line.
 */
const LoopSegmentRow = ({ loopSegments, colorPalette, activityCatalog, wrap = false }) => {
  if (!Array.isArray(loopSegments) || loopSegments.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic py-1.5">No activities</div>
    );
  }

  return (
    <div
      className={`flex gap-2 py-1.5 pr-2 items-center ${wrap ? 'flex-wrap' : ''}`}
      style={wrap ? undefined : { minWidth: "max-content", whiteSpace: "nowrap" }}
    >
      {loopSegments.map((segment, segmentIndex) => {
        const codes = Array.isArray(segment.activity_codes)
          ? segment.activity_codes
          : (segment.activities || []).map((a) => a);
        const names = Array.isArray(segment.activities) ? segment.activities : codes;
        const isLoop = !!segment.is_loop;
        const countDisplay =
          segment.count_display !== undefined && segment.count_display !== null
            ? segment.count_display
            : segment.count;

        const blocks = codes.map((code, i) => {
          const name = activityCatalog?.[code]?.name || names[i] || code;
          const color = colorPalette?.[code] || "#94a3b8";
          return (
            <ActivityBlock
              key={`${segmentIndex}-${code}-${i}`}
              activityCode={code}
              activityName={name}
              color={color}
              index={i}
            />
          );
        });

        if (!isLoop) {
          return (
            <React.Fragment key={`seg-${segmentIndex}`}>{blocks}</React.Fragment>
          );
        }

        // Colour the frame and badge from the group's own chips, not a fixed hue.
        const accentColor = colorPalette?.[codes[0]] || "#94a3b8";
        const frameColor = tinycolor(accentColor).darken(8).toHexString();
        const frameWash = tinycolor(accentColor).setAlpha(0.1).toRgbString();
        const badgeTextColor = getContrastTextColor(accentColor);

        // Show the full repetition count (no cap).
        const badgeLabel = countDisplay;

        return (
          <div
            key={`seg-${segmentIndex}`}
            className="relative inline-flex items-center gap-0.5 rounded-md border-2 px-0.5 py-0.5"
            style={{ borderColor: frameColor, backgroundColor: frameWash }}
            title={
              typeof countDisplay !== "number"
                ? "Loop pattern — repetition count hidden (abstract mode)"
                : `Median repeats: ${countDisplay}` +
                  (segment.count_summary ? ` | ${segment.count_summary}` : "")
            }
          >
            {/* The chips, kept tight so they read as one connected group. */}
            {blocks}
            {/* Count badge on the corner — the negative offset tucks it into the
                row's padding so it isn't clipped in the collapsed view. */}
            <span
              className="absolute -top-1.5 -right-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full border border-white px-1 text-[9px] font-semibold leading-none shadow-sm"
              style={{ backgroundColor: accentColor, color: badgeTextColor }}
            >
              {badgeLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default LoopSegmentRow;
